import React, { useEffect, useState, useRef, Children, Component } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Hero } from '../components/Hero';
import { ProblemSolution } from '../components/ProblemSolution';
import { PageCTA } from '../components/PageCTA';
import { Layout, Smartphone, Share2, ArrowRight, BarChart3, Users, Zap, CheckCircle2 } from 'lucide-react';
// Animated Counter Component
function AnimatedCounter({
  value,
  prefix = '',
  suffix = ''
}: {
  value: number;
  prefix?: string;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const duration = 2000; // 2 seconds
      const increment = value / (duration / 16); // 60fps
      const timer = setInterval(() => {
        start += increment;
        if (start >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);
  return <span ref={ref} data-id="element-500">
      {prefix}
      {count}
      {suffix}
    </span>;
}
export function HomePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const services = [{
    icon: <Layout size={28} data-id="element-501" />,
    title: 'Página Web Profesional',
    description: 'Transformamos visitantes en clientes recurrentes con una web rápida, persuasiva y diseñada a medida para maximizar tus ventas.',
    metric: 'Conversión',
    metricValue: '+120%',
    chartType: 'bar'
  }, {
    icon: <Smartphone size={28} data-id="element-502" />,
    title: 'Menú Digital Avanzado',
    description: 'Aumenta el ticket medio con una carta digital interactiva, visualmente irresistible y optimizada para una experiencia de usuario perfecta.',
    metric: 'Interacción',
    metricValue: '3x',
    chartType: 'donut'
  }, {
    icon: <Share2 size={28} data-id="element-503" />,
    title: 'Redes Sociales Sincronizadas',
    description: 'Multiplica tu alcance y autoridad con perfiles sociales estratégicos que conectan con tu audiencia y dirigen tráfico directo a tu negocio.',
    metric: 'Alcance',
    metricValue: '+85%',
    chartType: 'line'
  }];
  const metrics = [{
    icon: <BarChart3 size={32} data-id="element-504" />,
    value: <AnimatedCounter value={70} prefix="+" suffix="%" data-id="element-505" />,
    label: 'Más visibilidad online'
  }, {
    icon: <Users size={32} data-id="element-506" />,
    value: <AnimatedCounter value={3} prefix="+" suffix="x" data-id="element-507" />,
    label: 'Más interacción con clientes'
  }, {
    icon: <Zap size={32} data-id="element-508" />,
    value: <AnimatedCounter value={100} suffix="%" data-id="element-509" />,
    label: 'Tasa de conversión optimizada'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <div data-id="element-510">
      <Hero data-id="element-511" />
      <ProblemSolution data-id="element-512" />

      {/* Services Overview */}
      <section className="py-24 bg-[#0a0a0a] relative border-t border-white/[0.05]" data-id="element-513">
        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-514">
          <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-515">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-516">
              <h2 className="text-3xl md:text-4xl font-medium text-white mb-4 tracking-tight" data-id="element-517">
                Un ecosistema digital completo
              </h2>
              <p className="text-gray-400 text-[15px] leading-relaxed" data-id="element-518">
                No hacemos piezas sueltas. Construimos una maquinaria donde cada
                elemento trabaja en conjunto para generar resultados.
              </p>
            </motion.div>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16" data-id="element-519">
            {services.map((service, index) => <motion.div key={index} variants={itemVariants} className="card-premium p-8 flex flex-col h-full relative" data-id="element-520">
                <div className="absolute top-6 right-6 text-[11px] font-medium text-gray-600 select-none" data-id="element-521">
                  0{index + 1}
                </div>
                <div className="icon-premium mb-6" data-id="element-522">{service.icon}</div>
                <h3 className="text-lg font-medium text-white mb-3" data-id="element-523">
                  {service.title}
                </h3>
                <p className="text-gray-400 text-[14px] leading-relaxed mb-8 flex-grow" data-id="element-524">
                  {service.description}
                </p>

                {/* Visual Metric */}
                <div className="mt-auto pt-6 h-px w-full bg-white/[0.06] mb-4" data-id="element-525"></div>
                <div className="mt-auto" data-id="element-526">
                  <div className="flex items-center justify-between mb-4" data-id="element-527">
                    <span className="badge-premium" data-id="element-528">{service.metric}</span>
                    <span className="text-[15px] font-medium text-white" data-id="element-529">
                      {service.metricValue}
                    </span>
                  </div>

                  <div className="h-10 w-full flex items-end justify-center" data-id="element-530">
                    {service.chartType === 'bar' && <div className="flex items-end gap-1.5 w-full h-full" data-id="element-531">
                        {[30, 50, 40, 70, 60, 100].map((h, i) => <motion.div key={i} initial={{
                    height: 0
                  }} whileInView={{
                    height: `${h}%`
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.8,
                    delay: i * 0.1
                  }} className="flex-1 bg-brand-emerald rounded-sm opacity-80" data-id="element-532" />)}
                      </div>}
                    {service.chartType === 'donut' && <div className="relative w-12 h-12 mx-auto" data-id="element-533">
                        <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90" data-id="element-534">
                          <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" data-id="element-535" />
                          <motion.circle initial={{
                      strokeDasharray: '0 100'
                    }} whileInView={{
                      strokeDasharray: '75 100'
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1.5,
                      ease: 'easeOut'
                    }} cx="18" cy="18" r="16" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" data-id="element-536" />
                        </svg>
                      </div>}
                    {service.chartType === 'line' && <svg viewBox="0 0 100 40" className="w-full h-full overflow-visible" data-id="element-537">
                        <motion.path initial={{
                    pathLength: 0
                  }} whileInView={{
                    pathLength: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1.5,
                    ease: 'easeInOut'
                  }} d="M0,30 Q20,35 40,20 T80,15 T100,5" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" data-id="element-538" />
                        <motion.circle initial={{
                    scale: 0
                  }} whileInView={{
                    scale: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    delay: 1.5
                  }} cx="100" cy="5" r="3" fill="#fff" data-id="element-539" />
                      </svg>}
                  </div>
                </div>
              </motion.div>)}
          </motion.div>

          {/* Simple CTA Banner replacing the redundant pricing section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-4xl mx-auto mb-16 card-premium p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-540">
            <div className="flex-1 text-center md:text-left" data-id="element-541">
              <h3 className="text-xl md:text-2xl font-medium text-white mb-2 tracking-tight" data-id="element-542">
                Todo esto por <span className="text-gradient" data-id="element-543">350€</span>{' '}
                <span className="text-gray-500 font-normal" data-id="element-544">
                  — Sin sorpresas
                </span>
              </h3>
              <p className="text-gray-400 text-[14px] leading-relaxed" data-id="element-545">
                Un único pago dividido en dos fases. Comenzamos a trabajar el
                mismo día del abono inicial.
              </p>
            </div>
            <div className="shrink-0 w-full md:w-auto" data-id="element-546">
              <Link to="/contacto" className="inline-flex justify-center items-center gap-2 bg-white/[0.03] border border-white/[0.08] hover:bg-white/[0.06] text-white w-full md:w-auto px-6 py-3.5 rounded-xl font-medium text-[14px] transition-colors group" data-id="element-547">
                Empezar ahora
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform text-gray-400" data-id="element-548" />
              </Link>
            </div>
          </motion.div>

          <div className="text-center" data-id="element-549">
            <Link to="/servicios" className="inline-flex items-center gap-2 text-gray-400 hover:text-white font-medium text-[14px] transition-colors group" data-id="element-550">
              Descubre nuestro sistema en detalle
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-551" />
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Results Preview */}
      <section className="py-24 bg-[#0a0a0a] relative border-t border-white/[0.05]" data-id="element-552">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl relative z-10" data-id="element-553">
          <div className="card-premium p-8 md:p-12" data-id="element-554">
            <div className="flex flex-col lg:flex-row gap-12 items-center" data-id="element-555">
              <div className="w-full lg:w-1/3" data-id="element-556">
                <h2 className="text-3xl md:text-4xl font-medium text-white mb-4 tracking-tight" data-id="element-557">
                  Resultados que puedes medir
                </h2>
                <p className="text-gray-400 mb-8 text-[15px] leading-relaxed" data-id="element-558">
                  Nuestra metodología está diseñada para impactar directamente
                  en el crecimiento de tu negocio, con métricas claras y
                  transparentes.
                </p>
                <Link to="/resultados" className="inline-flex items-center gap-2 text-brand-emerald hover:text-brand-emeraldLight font-medium text-sm transition-colors group" data-id="element-559">
                  Ver todos los resultados
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-560" />
                </Link>
              </div>

              <div className="w-full lg:w-2/3 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/[0.05] rounded-xl overflow-hidden border border-white/[0.05]" data-id="element-561">
                {metrics.map((metric, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="bg-[#111] p-6 flex flex-col items-center justify-center text-center hover:bg-[#131313] transition-colors" data-id="element-562">
                    <div className="icon-premium mb-4 scale-75" data-id="element-563">
                      {metric.icon}
                    </div>
                    <div className="text-3xl font-medium text-gradient mb-2 tracking-tight" data-id="element-564">
                      {metric.value}
                    </div>
                    <div className="text-[11px] font-medium text-gray-400 uppercase tracking-widest" data-id="element-565">
                      {metric.label}
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <PageCTA data-id="element-566" />
    </div>;
}